/**
 * ReviewCriteriaPanel — the study-side lifecycle for AI eligibility review
 * criteria (docs/crm-eligibility-review.md §5/§9). Lives on StudyDetail near
 * the prescreen surfaces.
 *
 * Lifecycle surfaces (mirroring TextArtifactPanel's published-vs-working split):
 *   - CONFIRMED card: the immutable, review-driving criteria set (green banner
 *     with version + confirmed date/by). "Extract new version" starts an
 *     amendment cycle; the confirmed set stays live until the new one confirms.
 *   - WORKING card below it: extracting spinner (4s poll) / editable draft
 *     with an amber "needs confirmation" banner / failed + retry. Confirming a
 *     draft requires typing CONFIRM — these criteria become the basis for AI
 *     eligibility reviews of leads.
 *
 * Reading is member-visible (any CRM user can RUN a review, so they can see
 * what the AI checks against): non-admins fetch and get the same cards
 * read-only — confirmed banner + list, drafts marked "awaiting admin
 * confirmation" — with every mutation affordance (extract / edit / add /
 * delete / confirm) hidden. Mutation routes stay admin-gated server-side;
 * 403s (stale role) surface gracefully.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ListChecks,
  Loader2,
  Pencil,
  Plus,
  ShieldCheck,
  Sparkles,
  Trash2,
} from 'lucide-react';
import api from '../../../services/api';
import { relTime } from '../../lib';
import { useConfirm, Z } from '../ui';
import {
  REVIEW_CRITERION_CATEGORIES,
  ReviewCriterionCategory,
  ReviewCriterionKind,
  StudyReviewCriteriaSet,
  StudyReviewCriterion,
  reviewCategoryLabel,
} from './criteriaTypes';
import CriterionRuleEditor, {
  CompiledRuleFields,
  CriterionRuleSummary,
  compiledFieldsFrom,
} from './CriterionRuleEditor';
import { VersionHistoryButton } from '../versions';

interface ReviewCriteriaPanelProps {
  studyId: string;
  isAdmin: boolean;
  /** Study has ≥1 fully-extracted document — extraction needs a protocol/ICF. */
  hasExtractedDoc: boolean;
}

const errMsg = (e: any): string => {
  if (e?.response?.status === 403) return 'Only CRM administrators can manage AI review criteria.';
  return e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Request failed';
};

const fmtDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

/** Criteria of one kind, in display order (sort_order, then criterion number). */
function criteriaOfKind(set: StudyReviewCriteriaSet, kind: ReviewCriterionKind): StudyReviewCriterion[] {
  return (set.criteria || [])
    .filter((c) => c.criterion_kind === kind)
    .sort((a, b) =>
      a.sort_order - b.sort_order ||
      a.criterion_number.localeCompare(b.criterion_number, undefined, { numeric: true }));
}

const SET_STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  extracting: { label: 'Extracting', cls: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300' },
  draft: { label: 'Draft', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  confirmed: { label: 'Confirmed', cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  retired: { label: 'Retired', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
};

// ---------------------------------------------------------------------------
// Criterion display row
// ---------------------------------------------------------------------------

function CriterionRow({ criterion, editable, disabled, onEdit, onDelete }: {
  criterion: StudyReviewCriterion;
  editable: boolean;
  disabled: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <li className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono text-xs font-semibold text-primary-700 dark:text-primary-400">
              {criterion.criterion_number}
            </span>
            <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              {reviewCategoryLabel(criterion.category)}
            </span>
            {criterion.is_critical && (
              <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
                Critical
              </span>
            )}
            {criterion.source_page != null && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500">protocol p.{criterion.source_page}</span>
            )}
          </div>
          <p className="mt-1 text-sm leading-snug text-gray-900 dark:text-gray-100">{criterion.criterion_text}</p>
          {criterion.plain_language && (
            <p className="mt-0.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{criterion.plain_language}</p>
          )}
          <div className="mt-1.5">
            <CriterionRuleSummary criterion={criterion} />
          </div>
        </div>
        {editable && (
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={onEdit}
              disabled={disabled}
              aria-label={`Edit criterion ${criterion.criterion_number}`}
              className="rounded-lg p-2 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-700 dark:hover:text-primary-400"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={disabled}
              aria-label={`Delete criterion ${criterion.criterion_number}`}
              className="rounded-lg p-2 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-700"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Criterion editor (shared by inline edit + add)
// ---------------------------------------------------------------------------

interface CriterionFields {
  criterion_kind: ReviewCriterionKind;
  category: ReviewCriterionCategory;
  criterion_text: string;
  plain_language: string | null;
  is_critical: boolean;
  // Facts-engine compiled predicate — rides the same PATCH/POST body; the
  // backend validates leniently on this path (sanitizeCriterionInput handles
  // both the extraction-authored shape and hand edits here identically).
  rule: CompiledRuleFields['rule'];
  rule_facts: CompiledRuleFields['rule_facts'];
  applies_to: CompiledRuleFields['applies_to'];
  qualifiers: CompiledRuleFields['qualifiers'];
  tier_hint: CompiledRuleFields['tier_hint'];
}

function CriterionEditor({ initial, initialKind, saving, onSave, onCancel }: {
  initial?: StudyReviewCriterion;
  initialKind: ReviewCriterionKind;
  saving: boolean;
  onSave: (fields: CriterionFields) => void;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<ReviewCriterionKind>(initial?.criterion_kind || initialKind);
  const [category, setCategory] = useState<ReviewCriterionCategory>(initial?.category || 'other');
  const [text, setText] = useState(initial?.criterion_text || '');
  const [plain, setPlain] = useState(initial?.plain_language || '');
  const [critical, setCritical] = useState(initial?.is_critical || false);
  const [compiled, setCompiled] = useState<CompiledRuleFields>(() => compiledFieldsFrom(initial || {}));

  const save = () => {
    if (!text.trim()) return;
    onSave({
      criterion_kind: kind,
      category,
      criterion_text: text.trim(),
      plain_language: plain.trim() || null,
      is_critical: critical,
      rule: compiled.rule,
      rule_facts: compiled.rule_facts,
      applies_to: compiled.applies_to,
      qualifiers: compiled.qualifiers,
      tier_hint: compiled.tier_hint,
    });
  };

  return (
    <div className="rounded-lg border border-primary-200 bg-primary-50/40 p-3 dark:border-primary-800 dark:bg-primary-900/10">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Kind</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as ReviewCriterionKind)}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          >
            <option value="inclusion">Inclusion</option>
            <option value="exclusion">Exclusion</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Category</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as ReviewCriterionCategory)}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          >
            {REVIEW_CRITERION_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </label>
      </div>
      <label className="mt-2 block">
        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
          Criterion text <span className="text-rose-500">*</span>
          <span className="ml-1 font-normal text-gray-400">(verbatim protocol wording)</span>
        </span>
        <textarea
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 2000))}
          placeholder="Exact wording from the protocol…"
          className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        />
      </label>
      <label className="mt-2 block">
        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
          Plain language <span className="font-normal text-gray-400">(optional restatement for recruiters)</span>
        </span>
        <textarea
          rows={2}
          value={plain}
          onChange={(e) => setPlain(e.target.value.slice(0, 2000))}
          placeholder="What this means in everyday terms…"
          className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        />
      </label>
      <label className="mt-2 inline-flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={critical}
          onChange={(e) => setCritical(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900"
        />
        <span className="text-sm text-gray-700 dark:text-gray-300">Critical criterion</span>
      </label>

      <div className="mt-3">
        <CriterionRuleEditor fields={compiled} onChange={setCompiled} />
      </div>

      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="min-h-[44px] rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !text.trim()}
          className="min-h-[44px] rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
        >
          {saving ? 'Saving…' : initial ? 'Save criterion' : 'Add criterion'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Type-to-confirm dialog
// ---------------------------------------------------------------------------

function ConfirmCriteriaDialog({ version, criteriaCount, busy, error, onConfirm, onCancel }: {
  version: number;
  criteriaCount: number;
  busy: boolean;
  /** Confirm failure — rendered inside the dialog so it is never hidden behind the overlay. */
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState('');
  const ready = typed === 'CONFIRM';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className={`fixed inset-0 ${Z.modal} flex items-start justify-center overflow-y-auto bg-black/40 p-4`}
      onClick={onCancel}
      role="presentation"
    >
      <div
        className="my-auto w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Confirm review criteria v${version}`}
      >
        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <ShieldCheck className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          Confirm review criteria v{version}
        </h2>
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          These {criteriaCount} criteria become the basis for AI eligibility reviews of leads against this
          study. Confirmed criteria are immutable — changing them later requires extracting or authoring a
          new version. Review every criterion against the protocol before confirming.
        </div>
        <label className="mt-4 block text-xs font-medium text-gray-600 dark:text-gray-300">
          Type <span className="font-mono font-semibold">CONFIRM</span> to proceed
        </label>
        <input
          type="text"
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="CONFIRM"
          autoComplete="off"
          className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        />
        {error && (
          <div
            role="alert"
            className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
          >
            {error}
          </div>
        )}
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="min-h-[44px] rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!ready || busy}
            className="min-h-[44px] rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
          >
            {busy ? 'Confirming…' : 'Confirm criteria'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export default function ReviewCriteriaPanel({ studyId, isAdmin, hasExtractedDoc }: ReviewCriteriaPanelProps) {
  const confirm = useConfirm();
  const [sets, setSets] = useState<StudyReviewCriteriaSet[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [showConfirmedList, setShowConfirmedList] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  /** Confirm-dialog failure — rendered INSIDE the dialog (the panel-level
   *  actionError would sit behind the dialog's fixed overlay). */
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingKind, setAddingKind] = useState<ReviewCriterionKind | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get(`/api/crm/studies/${studyId}/review-criteria`);
      setSets(res.data?.sets || []);
      setLoadError(null);
    } catch (err: any) {
      if (!silent) setLoadError(errMsg(err));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [studyId]);

  // Members and admins both read the criteria (the GET is member-visible).
  useEffect(() => { load(); }, [load]);

  // At most one open (extracting/draft) set and one confirmed set exist per
  // study (partial unique indexes); a failed newest set gets a retry surface.
  const openSet = useMemo(() => (sets || []).find((s) => s.status === 'extracting' || s.status === 'draft') || null, [sets]);
  const confirmedSet = useMemo(() => (sets || []).find((s) => s.status === 'confirmed') || null, [sets]);
  const newestSet = useMemo(
    () => (sets || []).slice().sort((a, b) => b.version - a.version)[0] || null,
    [sets],
  );
  const failedSet = !openSet && newestSet?.status === 'failed' ? newestSet : null;

  // 4s poll while an extraction is running so the card flips to draft/failed
  // without a manual refresh (StudyDetail precedent).
  const extracting = openSet?.status === 'extracting';
  useEffect(() => {
    if (!extracting) return;
    const timer = window.setInterval(() => load(true), 4000);
    return () => window.clearInterval(timer);
  }, [extracting, load]);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setActionError(null);
    try {
      await fn();
      await load(true);
    } catch (err: any) {
      setActionError(errMsg(err));
    } finally {
      setBusy(null);
    }
  };

  const extract = () => run('extract', () =>
    api.post(`/api/crm/studies/${studyId}/review-criteria/extract`, {}));

  // Confirm runs OUTSIDE run(): its failure must render inside the still-open
  // dialog (with the button re-enabled), not in the panel behind the overlay.
  const confirmSet = async () => {
    if (!openSet || busy !== null) return;
    setBusy('confirm');
    setConfirmError(null);
    try {
      await api.post(`/api/crm/review-criteria-sets/${openSet.id}/confirm`, {});
      setConfirmOpen(false);
      setShowConfirmedList(false);
      await load(true);
    } catch (err: any) {
      setConfirmError(errMsg(err));
    } finally {
      setBusy(null);
    }
  };

  const saveCriterion = (criterionId: string, fields: CriterionFields) =>
    run(`save:${criterionId}`, async () => {
      await api.patch(`/api/crm/review-criteria/${criterionId}`, fields);
      setEditingId(null);
    });

  const addCriterion = (setId: string, fields: CriterionFields) =>
    run(`add:${fields.criterion_kind}`, async () => {
      await api.post(`/api/crm/review-criteria-sets/${setId}/criteria`, fields);
      setAddingKind(null);
    });

  const deleteCriterion = async (criterion: StudyReviewCriterion) => {
    if (!(await confirm({
      title: `Delete criterion ${criterion.criterion_number}?`,
      message: 'This only affects the draft — nothing already confirmed changes.',
      confirmLabel: 'Delete',
      danger: true,
    }))) return;
    run(`delete:${criterion.id}`, () => api.delete(`/api/crm/review-criteria/${criterion.id}`));
  };

  if (loading) {
    return (
      <div className="flex h-24 items-center justify-center rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
      >
        {loadError}
      </div>
    );
  }

  const extractBtn = (label: string) => (
    <button
      type="button"
      onClick={extract}
      disabled={busy !== null || !hasExtractedDoc || !!openSet}
      title={!hasExtractedDoc ? 'Upload a protocol/ICF in Documents first' : openSet ? 'An extraction or draft is already open' : undefined}
      className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-800 sm:min-h-0"
    >
      <Sparkles className="h-4 w-4" /> {busy === 'extract' ? 'Starting…' : label}
    </button>
  );

  const criteriaGroup = (set: StudyReviewCriteriaSet, kind: ReviewCriterionKind, editable: boolean) => {
    const rows = criteriaOfKind(set, kind);
    const heading = kind === 'inclusion' ? 'Inclusions' : 'Exclusions';
    return (
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
          {heading} ({rows.length})
        </h4>
        <ul className="mt-2 space-y-2">
          {rows.map((c) => (
            editable && editingId === c.id ? (
              <li key={c.id}>
                <CriterionEditor
                  initial={c}
                  initialKind={kind}
                  saving={busy === `save:${c.id}`}
                  onSave={(fields) => saveCriterion(c.id, fields)}
                  onCancel={() => setEditingId(null)}
                />
              </li>
            ) : (
              <CriterionRow
                key={c.id}
                criterion={c}
                editable={editable}
                disabled={busy !== null}
                onEdit={() => { setAddingKind(null); setEditingId(c.id); }}
                onDelete={() => deleteCriterion(c)}
              />
            )
          ))}
          {rows.length === 0 && !(editable && addingKind === kind) && (
            <li className="rounded-lg border border-dashed border-gray-200 p-3 text-sm text-gray-400 dark:border-gray-700 dark:text-gray-500">
              No {heading.toLowerCase()} yet.
            </li>
          )}
          {editable && addingKind === kind && (
            <li>
              <CriterionEditor
                initialKind={kind}
                saving={busy === `add:${kind}`}
                onSave={(fields) => addCriterion(set.id, fields)}
                onCancel={() => setAddingKind(null)}
              />
            </li>
          )}
        </ul>
        {editable && addingKind !== kind && (
          <button
            type="button"
            onClick={() => { setEditingId(null); setAddingKind(kind); }}
            disabled={busy !== null}
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:border-primary-400 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:border-primary-600 dark:hover:text-primary-400"
          >
            <Plus className="h-4 w-4" /> Add {kind} criterion
          </button>
        )}
      </div>
    );
  };

  const draftCount = openSet?.status === 'draft' ? (openSet.criteria || []).length : 0;

  return (
    <div className="space-y-4">
      {/* ---- Confirmed set (the review-driving criteria) — or empty state ---- */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
              <ListChecks className="h-4 w-4 text-primary-600 dark:text-primary-400" /> AI Review Criteria
            </h2>
            {confirmedSet && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">
                <CheckCircle2 className="h-3 w-3" /> Confirmed v{confirmedSet.version}
              </span>
            )}
          </div>
          {isAdmin && !openSet && extractBtn(confirmedSet ? 'Extract new version' : 'Extract from protocol')}
        </div>

        {actionError && (
          <div
            role="alert"
            className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
          >
            {actionError}
          </div>
        )}

        {confirmedSet ? (
          <>
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300">
              Version {confirmedSet.version} confirmed {fmtDate(confirmedSet.confirmed_at)}
              {confirmedSet.confirmed_by_name ? ` by ${confirmedSet.confirmed_by_name}` : ''} — AI eligibility
              reviews against this study use these criteria. After a protocol amendment, extract a new version.
            </div>
            <button
              type="button"
              onClick={() => setShowConfirmedList((v) => !v)}
              className="mt-3 text-sm font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
            >
              {showConfirmedList
                ? 'Hide criteria'
                : `Show ${(confirmedSet.criteria || []).length} criteria`}
            </button>
            {showConfirmedList && (
              <div className="mt-3 space-y-4">
                {criteriaGroup(confirmedSet, 'inclusion', false)}
                {criteriaGroup(confirmedSet, 'exclusion', false)}
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {isAdmin ? (
              <>
                No review criteria yet. Extract inclusion/exclusion criteria from the study&rsquo;s protocol
                documents, review the draft, and confirm it — confirmed criteria are what the AI checks each
                lead&rsquo;s medical history against.
              </>
            ) : (
              <>
                No review criteria yet. A CRM administrator extracts inclusion/exclusion criteria from the
                study&rsquo;s protocol documents and confirms them — confirmed criteria are what the AI checks
                each lead&rsquo;s medical history against.
              </>
            )}
          </p>
        )}

        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          AI eligibility reviews are a pre-screening aid, not a medical or enrollment determination.
        </p>
      </div>

      {/* ---- Working set: extracting / draft / failed ---- */}
      {openSet?.status === 'extracting' && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
          <div className="flex items-center gap-3 rounded-lg bg-primary-50 p-4 dark:bg-primary-900/20">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
            <p className="text-sm text-primary-800 dark:text-primary-200">
              Extracting criteria (v{openSet.version}) from the study documents — this can take a few minutes…
            </p>
          </div>
        </div>
      )}

      {openSet?.status === 'draft' && (
        <div className="rounded-xl border border-amber-200 bg-white p-4 shadow-sm dark:border-amber-800/60 dark:bg-gray-800 sm:p-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Draft v{openSet.version}</h3>
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                {isAdmin
                  ? 'Needs confirmation — not used for reviews yet'
                  : 'Awaiting admin confirmation — not used for reviews yet'}
              </span>
            </div>
            {isAdmin && (
              <button
                type="button"
                onClick={() => { setConfirmError(null); setConfirmOpen(true); }}
                disabled={busy !== null || draftCount === 0}
                title={draftCount === 0 ? 'Add at least one criterion first' : undefined}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-800 sm:min-h-0"
              >
                <ShieldCheck className="h-4 w-4" /> Confirm criteria…
              </button>
            )}
          </div>
          <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
            {isAdmin ? (
              <>
                Check every criterion against the protocol — edit wording, fix categories, mark critical items,
                add anything the AI missed. Confirming locks this version and makes it the basis for AI
                eligibility reviews.
              </>
            ) : (
              <>
                A CRM administrator is reviewing this draft against the protocol. Reviews keep using the
                confirmed version (if any) until this draft is confirmed.
              </>
            )}
          </p>
          <div className="space-y-5">
            {criteriaGroup(openSet, 'inclusion', isAdmin)}
            {criteriaGroup(openSet, 'exclusion', isAdmin)}
          </div>
        </div>
      )}

      {failedSet && (
        <div className="rounded-xl border border-red-200 bg-white p-4 shadow-sm dark:border-red-900/50 dark:bg-gray-800 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
              <p role="alert" className="text-sm text-red-700 dark:text-red-300">
                Criteria extraction (v{failedSet.version}) failed: {failedSet.error || 'unknown error'}
              </p>
            </div>
            {isAdmin && extractBtn('Try again')}
          </div>
        </div>
      )}

      {/* ---- Versions ---- */}
      {(sets || []).length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Versions</h3>
            {/* Diff any two versions + who extracted/edited/confirmed what.
                Self-hides for non-CRM-admins. */}
            <VersionHistoryButton
              kind="review_criteria"
              params={{ study_id: studyId }}
              title="Eligibility review criteria"
              variant="link"
              label="Compare & audit trail"
            />
          </div>
          <ul className="mt-2 divide-y divide-gray-100 dark:divide-gray-700">
            {(sets || []).slice().sort((a, b) => b.version - a.version).map((s) => {
              const badge = SET_STATUS_BADGE[s.status] || SET_STATUS_BADGE.retired;
              return (
                <li key={s.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                  <span className="font-medium text-gray-900 dark:text-gray-100">v{s.version}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.cls}`}>{badge.label}</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    created {relTime(s.created_at)} ago
                    {s.status === 'confirmed' && s.confirmed_at ? ` · confirmed ${fmtDate(s.confirmed_at)}` : ''}
                  </span>
                  {/* Opens the history on Compare: this version vs the newest.
                      Extracting / failed sets carry no criteria and are not in
                      the history, so they get no link. */}
                  {s.status !== 'extracting' && s.status !== 'failed' && (
                    <VersionHistoryButton
                      kind="review_criteria"
                      params={{ study_id: studyId }}
                      title="Eligibility review criteria"
                      variant="link"
                      initialVersionId={s.id}
                      label="Compare"
                      className="ml-auto"
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {confirmOpen && openSet?.status === 'draft' && (
        <ConfirmCriteriaDialog
          version={openSet.version}
          criteriaCount={draftCount}
          busy={busy === 'confirm'}
          error={confirmError}
          onConfirm={confirmSet}
          onCancel={() => { setConfirmOpen(false); setConfirmError(null); }}
        />
      )}
    </div>
  );
}
